import React from 'react';
import { Box, Heading, Text, HStack, VStack, Image, Icon } from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { FaPlay } from 'react-icons/fa';
import { useAudioPlayer } from '../context/AudioPlayerContext';
import { formatDate, formatDuration } from '../utils/format';

const CARD_W_BASE = 160;
const CARD_W_MD   = 200;
const GAP         = 16;

const RANK_STYLE = { bg: 'rgba(21,13,4,0.78)', color: 'rgba(255,255,255,0.85)', shadow: 'rgba(0,0,0,0.3)' };

const WaveformBars = () => (
  <Box display="flex" alignItems="flex-end" gap="3px" h="22px" px="2px">
    {[0, 1, 2, 3].map((i) => (
      <Box
        key={i}
        w="3px"
        borderRadius="2px"
        bg="rgba(255,152,0,0.9)"
        sx={{
          animation: `waveform-bar 0.9s ease-in-out infinite`,
          animationDelay: `${i * 0.15}s`,
          height: '40%',
        }}
      />
    ))}
  </Box>
);

const MarqueeCard = ({ ep, isSelected, isPlaying, onClick }) => (
  <Box
    role="group"
    flexShrink={0}
    w={{ base: `${CARD_W_BASE}px`, md: `${CARD_W_MD}px` }}
    borderRadius="xl"
    overflow="hidden"
    border="1px solid"
    borderColor={isSelected ? 'rgba(255,152,0,0.8)' : 'rgba(255,150,50,0.1)'}
    bg="rgba(255,150,50,0.04)"
    boxShadow={isSelected ? '0 0 30px rgba(255,152,0,0.35), 0 20px 50px rgba(255,152,0,0.2)' : 'none'}
    transition="all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)"
    transform={isSelected ? 'scale(1.06) translateY(-6px)' : undefined}
    zIndex={isSelected ? 2 : 1}
    cursor="pointer"
    onClick={onClick}
    _hover={!isSelected ? {
      borderColor: 'rgba(255,152,0,0.7)',
      transform: 'scale(1.06) translateY(-6px)',
      boxShadow: '0 20px 50px rgba(255,152,0,0.3), 0 0 0 1px rgba(255,152,0,0.2)',
      zIndex: 2,
    } : {}}
  >
    {/* Square image */}
    <Box position="relative" w="full" paddingTop="100%" overflow="hidden">
      <Image
        src={ep.imageUrl}
        alt={ep.title}
        position="absolute"
        top={0} left={0}
        w="full" h="full"
        objectFit="cover"
        transition="transform 0.4s ease"
        transform={isSelected ? 'scale(1.08)' : undefined}
        _groupHover={!isSelected ? { transform: 'scale(1.08)' } : {}}
        fallback={<Box w="full" h="full" bg="rgba(255,150,50,0.1)" position="absolute" top={0} left={0} />}
      />

      {/* Amber gradient overlay */}
      <Box
        position="absolute" inset={0}
        bgGradient="linear(to-t, rgba(255,152,0,0.45), transparent)"
        opacity={isSelected ? 1 : 0}
        transition="opacity 0.3s"
        _groupHover={{ opacity: 1 }}
      />

      {/* Rank badge — top left (fan favourites only) */}
      {ep.rank && (
        <Box
          position="absolute"
          top={2} left={2}
          minW="28px" h="28px"
          borderRadius="md"
          display="flex"
          alignItems="center"
          justifyContent="center"
          px={1}
          background={RANK_STYLE.bg}
          boxShadow={`0 2px 8px ${RANK_STYLE.shadow}`}
          backdropFilter="blur(4px)"
          zIndex={3}
        >
          <Text
            fontSize="11px"
            fontWeight="extrabold"
            color={RANK_STYLE.color}
            lineHeight={1}
            letterSpacing="-0.3px"
          >
            #{ep.rank}
          </Text>
        </Box>
      )}

      {/* Play button / waveform */}
      <Box
        position="absolute" inset={0}
        display="flex" alignItems="center" justifyContent="center"
        opacity={isSelected ? 1 : 0}
        transition="opacity 0.25s"
        _groupHover={{ opacity: 1 }}
      >
        {isSelected && isPlaying ? (
          <Box
            w="44px" h="44px"
            borderRadius="full"
            bg="rgba(21,13,4,0.75)"
            display="flex" alignItems="center" justifyContent="center"
            boxShadow="0 4px 20px rgba(0,0,0,0.4)"
          >
            <WaveformBars />
          </Box>
        ) : (
          <Box
            w="44px" h="44px"
            borderRadius="full"
            bg="rgba(255,152,0,0.9)"
            display="flex" alignItems="center" justifyContent="center"
            boxShadow="0 4px 20px rgba(0,0,0,0.4)"
            transition="transform 0.2s"
            _hover={{ transform: 'scale(1.1)' }}
          >
            <Icon as={FaPlay} color="#150d04" boxSize={4} ml="2px" />
          </Box>
        )}
      </Box>

      {/* Pulsing ring when playing */}
      {isSelected && isPlaying && (
        <Box
          position="absolute" inset={0}
          borderRadius="inherit"
          border="2px solid rgba(255,152,0,0.5)"
          sx={{ animation: 'pulse-ring-fade 1.8s ease-out infinite' }}
          pointerEvents="none"
        />
      )}
    </Box>

    {/* Meta — full bottom section is a nav link */}
    <RouterLink
      to={`/episodes?search=${encodeURIComponent(ep.title)}`}
      onClick={e => e.stopPropagation()}
    >
      <VStack
        align="start" spacing={1} p={3}
        transition="background 0.2s"
        _hover={{ bg: 'rgba(255,152,0,0.08)' }}
      >
        <Text
          fontSize="xs"
          fontWeight="semibold"
          color={isSelected ? 'brand.300' : 'whiteAlpha.900'}
          noOfLines={2}
          lineHeight="snug"
          transition="color 0.2s"
          _groupHover={{ color: 'brand.300' }}
        >
          {ep.title}
        </Text>
        <HStack spacing={2}>
          <Text fontSize="xs" color="whiteAlpha.500">
            {formatDate(ep.pubDate)}
          </Text>
          <Text fontSize="xs" color="brand.400">
            {formatDuration(ep.duration)}
          </Text>
        </HStack>
      </VStack>
    </RouterLink>
  </Box>
);

/**
 * Shared marquee of episode cards driven by the global audio player.
 * Props:
 *  - heading / headingRight: section header content
 *  - episodes: normalized episodes ({id,title,audioUrl,imageUrl,durationSec,pubDate,duration,rank?})
 *  - trackName: unique keyframe/class prefix per instance (e.g. "latest", "fav")
 *  - speedSec / mobileSpeedSec: marquee loop durations
 */
const EpisodeMarquee = ({ heading, headingRight, episodes, trackName, speedSec = 40, mobileSpeedSec = 32, py }) => {
  const { currentEpisode, isPlaying, playEpisode } = useAudioPlayer();

  const looped = [...episodes, ...episodes];
  const playingHere = isPlaying && episodes.some(e => e.id === currentEpisode?.id);
  const trackClass = `${trackName}-marquee-track`;

  return (
    <Box as="section" py={py ?? { base: 8, md: 10 }}>
      <Box className="glow-divider" mb={{ base: 4, md: 6 }} />

      <Box px={{ base: 4, md: 8, lg: 16 }}>
        <HStack justify="space-between" align="baseline" mb={{ base: 5, md: 6 }} maxW="container.xl" mx="auto">
          <Heading
            as="h2"
            fontSize={{ base: "2xl", md: "4xl" }}
            variant="gradient"
          >
            {heading}
          </Heading>
          {headingRight}
        </HStack>
      </Box>

      {/* Marquee — pauses on hover or while one of its episodes is playing */}
      <Box
        overflowX="hidden"
        py={5}
        sx={{
          [`&:hover .${trackClass}`]: { animationPlayState: 'paused' },
        }}
      >
        <Box
          className={trackClass}
          display="flex"
          gap={`${GAP}px`}
          sx={{
            width: 'max-content',
            [`@keyframes ${trackName}-marquee`]: {
              '0%':   { transform: 'translateX(0)' },
              '100%': { transform: `translateX(calc(-${episodes.length} * (${CARD_W_MD}px + ${GAP}px)))` },
            },
            [`@keyframes ${trackName}-marquee-mobile`]: {
              '0%':   { transform: 'translateX(0)' },
              '100%': { transform: `translateX(calc(-${episodes.length} * (${CARD_W_BASE}px + ${GAP}px)))` },
            },
            animation: `${trackName}-marquee ${speedSec}s linear infinite`,
            animationPlayState: playingHere ? 'paused' : 'running',
            // The shorthand inside the media query resets play-state, so re-declare it after
            '@media (max-width: 48em)': {
              animation: `${trackName}-marquee-mobile ${mobileSpeedSec}s linear infinite`,
              animationPlayState: playingHere ? 'paused' : 'running',
            },
          }}
        >
          {looped.map((ep, i) => (
            <MarqueeCard
              key={`${ep.id}-${i}`}
              ep={ep}
              isSelected={currentEpisode?.id === ep.id}
              isPlaying={isPlaying && currentEpisode?.id === ep.id}
              onClick={() => playEpisode(ep)}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default EpisodeMarquee;
